import { useCallback, useState } from 'react';
import { AgencyTaskSchema, PostSchema } from '@ametista-ai/api-schema';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Box,
  Checkbox,
  Group,
  Loader,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { openConfirmModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { UserBadge } from '@/components/Users/UserBadge';
import { getTaskBlockState } from '@/queries/posts/utils';
import { useDeletePostTask } from '@/queries/postTasks/useDeletePostTask';
import { useUpdatePostTask } from '@/queries/postTasks/useUpdatePostTask';
import { Icon } from '@/ui';
import { DueDateBadge } from './DueDateBadge';
import { PostChecklistItemForm } from './PostChecklistItemForm';
import classes from './PostChecklistItem.module.css';

export function PostChecklistItem({
  task,
  tasks,
  post,
}: {
  task: AgencyTaskSchema;
  tasks: AgencyTaskSchema[];
  post: Pick<PostSchema, 'approvedAt'>;
}) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const { mutateAsync: updateTask, isPending: isUpdating } = useUpdatePostTask();
  const { mutate: deleteTask, isPending: isDeleting } = useDeletePostTask();

  const handleCancel = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleChange = useCallback(
    async (changeTask: Partial<AgencyTaskSchema>) => {
      try {
        await updateTask({
          id: task.id,
          ...changeTask,
          postId: task.postId!,
        });
        setIsEditing(false);
      } catch {
        showNotification({
          title: 'Error saving task',
          message: 'Please try again',
          color: 'red',
        });
      }
    },
    [task, updateTask]
  );

  const handleToggleCompleted = useCallback(
    async (checked: boolean) => {
      try {
        await updateTask({
          id: task.id,
          postId: task.postId!,
          completedAt: checked ? new Date() : null,
        });
      } catch {
        showNotification({
          title: 'Error saving task',
          message: 'Please try again',
          color: 'red',
        });
      }
    },
    [task, updateTask]
  );

  const handleDelete = useCallback(() => {
    openConfirmModal({
      title: t('posts.delete-task'),
      children: <Text>{t('posts.delete-task-confirm', { taskTitle: task.title })}</Text>,
      labels: {
        confirm: t('posts.delete'),
        cancel: t('posts.cancel'),
      },
      onConfirm: () => {
        deleteTask(
          { id: task.id, postId: task.postId! },
          {
            onError: () => {
              showNotification({
                title: 'Error deleting task',
                message: 'Please try again',
                color: 'red',
              });
            },
          }
        );
      },
    });
  }, [t, task, deleteTask]);

  const { blockedByTasks, awaitingPostApproval, isBlocked } = getTaskBlockState(task, tasks, post);

  return (
    <Group
      gap="xs"
      justify="space-between"
      align="flex-start"
      className={classes.postChecklistItem}
      data-blocked={isBlocked ? 'true' : undefined}
    >
      <Group gap="xs" align="flex-start" flex={1}>
        <Box w={20} py={5}>
          <Checkbox
            checked={!!task.completedAt}
            disabled={isUpdating || isDeleting}
            onChange={(e) => handleToggleCompleted(e.currentTarget.checked)}
          />
        </Box>
        {isEditing ? (
          <PostChecklistItemForm
            tasks={tasks}
            value={task}
            onChange={handleChange}
            onCancel={handleCancel}
            isSaving={isUpdating}
          />
        ) : (
          <Tooltip
            disabled={!isBlocked}
            label={[
              blockedByTasks.length > 0 &&
                t('posts.blocked-by', { tasks: blockedByTasks.map((t) => t.title).join(', ') }),
              awaitingPostApproval && t('posts.blocked-by-approval'),
            ]
              .filter(Boolean)
              .join(' · ')}
          >
            <UnstyledButton flex={1} onClick={() => setIsEditing(true)} disabled={isDeleting}>
              <Group gap="xs" w="100%" h={30}>
                <Text size="sm" flex={1}>
                  {task.title}
                </Text>
                {task.dueDate && (
                  <DueDateBadge dueDate={task.dueDate} isPending={!task.completedAt} />
                )}
                {task.userId && <UserBadge userId={task.userId} />}
              </Group>
            </UnstyledButton>
          </Tooltip>
        )}
      </Group>
      {!isEditing && (
        <Group gap="xs" py={1}>
          {isDeleting ? (
            <Loader size="xs" />
          ) : (
            <ActionIcon
              variant="subtle"
              color="red"
              size="md"
              aria-label={t('posts.delete-task')}
              onClick={handleDelete}
              disabled={isUpdating}
            >
              <Icon name="trash" />
            </ActionIcon>
          )}
        </Group>
      )}
    </Group>
  );
}
